<template>
  <v-dialog
    v-if="featureEnabled"
    :value="value"
    width="560"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
    @input="handleInput"
  >
    <v-card class="aold__card pa-6 background-pattern-icon">
      <div class="d-flex align-center mb-4">
        <span class="aold__icon-chip">
          <Icon name="language_translate" color="purple" />
        </span>
        <v-spacer />
        <v-btn icon color="grey" aria-label="Close" @click="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <h2 id="aold-title" class="aold__title">Add operating language</h2>

      <div class="aold__picker">
        <ExSelectPicker
          :key="pickerResetKey"
          pickerLabel="Language"
          label="Select language"
          searchLabel="Search language"
          :items="languages"
          :isInitialLoading="catalogLoading"
          :useCode="false"
          :isSelectedItemRemove="true"
          :hasImageItem="false"
          noRecordMessage="No language found"
          @item-selected="onLanguageSelected"
          @clear-selection="onLanguageCleared"
        />
      </div>

      <p class="aold__caption">
        Languages already added to this account — active or retired — aren't shown here.
      </p>

      <div v-if="selectedLanguage" class="aold__disclosure">
        <p class="aold__disclosure-text">When confirmed, all videos, quizzes, and inspection protocols in the account will be gradually translated into the selected language in the background.</p>
        <p v-if="selectedLanguageSupportsDubbing" class="aold__disclosure-text">When the selected language supports AI dubbing, dubbed audio can be generated manually for videos from that operating language's row menu.</p>
      </div>

      <p v-if="errorMessage" class="aold__error" role="alert">{{ errorMessage }}</p>

      <div class="aold__footer">
        <StandardButton
          type="magenta"
          variant="outline"
          size="normal"
          label="Cancel"
          @click="handleClose"
        />
        <StandardButton
          type="magenta"
          variant="primary"
          size="normal"
          label="Add language"
          :disabled="!selectedLanguage || loading"
          :loaderLeft="loading"
          @click="handleAddLanguage"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import Icon from "@components/icons/Icon.vue";
import { LD_FLAG_IDEA_MDP_595 } from "@components/constants/launchDarklyFlagKeys";
import { getOperatingErrorMessage } from "@components/constants/operatingErrorCodes";

const props = defineProps({
  /** Controls dialog visibility (v-model open state). */
  value: {
    type: Boolean,
    default: false,
  },
  /**
   * Catalog of addable languages. The parent/service is expected to pre-filter
   * out any language that already has an OperatingLanguage row for the account
   * (Active or Retired) — unlike the Countries/Currencies pickers there is no
   * disabled-row pattern here: already-added languages are simply absent.
   */
  languages: {
    type: Array,
    default: () => [],
  },
  /** Shows a loading state on the Add language button while the request is in flight. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Shows the picker's loading skeleton (instead of "No language found") while the catalog request is in flight. */
  catalogLoading: {
    type: Boolean,
    default: false,
  },
  /**
   * Backend error code surfaced inline beneath the picker (e.g. a
   * LANGUAGE_ALREADY_ADDED-style code after a 400 from the add-operating-language
   * endpoint for a race condition). Mapped to copy via the shared operating
   * error map; empty clears the message.
   */
  errorCode: {
    type: String,
    default: "",
  },
  /**
   * Optional list (language codes, ids, or objects) that are considered capable
   * of Gemini text-to-speech / AI dubbing. When the selected language is in this
   * list — or carries its own supports_dubbing / supports_ai_dubbing flag — the
   * conditional AI-dubbing disclosure paragraph is shown.
   */
  dubbingSupportedLanguages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["onAdd", "input", "close"]);

const selectedLanguage = ref(null);
const errorMessage = ref("");
const featureEnabled = ref(false);

const proxy = getCurrentInstance()?.proxy;

/** Remount the picker on every open so a stale selection never leaks across opens. */
const pickerResetKey = computed(() => (props.value ? "open" : "closed"));

const LANGUAGE_DUBBING_BOOLEAN_KEYS = ["supports_ai_dubbing", "supports_dubbing"];

function languageCarriesDubbingFlag(language) {
  if (!language || typeof language !== "object") {
    return false;
  }
  return LANGUAGE_DUBBING_BOOLEAN_KEYS.some((key) => language[key] === true);
}

function languageInDubbingList(language) {
  if (!language || typeof language !== "object") {
    return false;
  }
  const id = language.id;
  const code = language.code;
  return (props.dubbingSupportedLanguages || []).some((entry) => {
    if (entry == null) {
      return false;
    }
    if (typeof entry === "object") {
      return (
        (entry.code && code && String(entry.code) === String(code)) ||
        (entry.id != null && id != null && String(entry.id) === String(id))
      );
    }
    return (code && String(entry) === String(code)) || (id != null && String(entry) === String(id));
  });
}

const selectedLanguageSupportsDubbing = computed(() => {
  const language = selectedLanguage.value;
  if (!language) {
    return false;
  }
  return languageCarriesDubbingFlag(language) || languageInDubbingList(language);
});

const onLanguageSelected = (language) => {
  selectedLanguage.value = language;
  errorMessage.value = "";
};

const onLanguageCleared = () => {
  selectedLanguage.value = null;
};

const handleAddLanguage = () => {
  if (!selectedLanguage.value || props.loading) {
    return;
  }
  emit("onAdd", selectedLanguage.value);
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleInput = (nextValue) => {
  emit("input", nextValue);
};

async function ensureFeatureEnabled() {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_595, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
}

// Reset selection + inline error each time the dialog is opened.
watch(
  () => props.value,
  (isOpen) => {
    if (isOpen) {
      selectedLanguage.value = null;
      errorMessage.value = "";
    }
  }
);

// Surface backend error codes (e.g. LANGUAGE_ALREADY_ADDED) reported by the
// parent after an API failure (race condition: added concurrently in another tab).
watch(
  () => props.errorCode,
  (code) => {
    errorMessage.value = code ? getOperatingErrorMessage(code) : "";
  }
);

onMounted(() => {
  ensureFeatureEnabled();
});
</script>

<style scoped>
.aold__card {
  background-color: #ffffff;
  border: 1px solid #e4e7ec;
  border-radius: 12px !important;
}

.aold__icon-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #f3edff;
}

.aold__icon-chip ::v-deep(svg) {
  width: 24px;
  height: 24px;
}

.aold__title {
  margin: 0 0 16px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.aold__caption {
  margin: 8px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 18px;
}

.aold__disclosure {
  margin-top: 12px;
}

.aold__disclosure-text {
  margin: 0 0 8px;
  color: #667085;
  font-size: 12px;
  line-height: 18px;
}

.aold__disclosure-text:last-child {
  margin-bottom: 0;
}

.aold__error {
  margin: 8px 0 0;
  color: #d92d20;
  font-size: 14px;
  line-height: 20px;
}

.aold__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0px 0px;
}

::v-deep(.select-picker) {
  padding: 0px !important;
}
</style>
